/**
 * SERVICIO DE AUTENTICACIÓN CENTRALIZADA VIA CORE API
 *
 * El Core API es la ÚNICA fuente de verdad para autenticación.
 * Las apps NO leen Supabase directamente.
 *
 * SINGLETON: resolveAuth() deduplicates concurrent calls.
 * Multiple consumers (EmpresaAuthContext, RequireDomainAccess)
 * share a single in-flight request + 30s cache.
 *
 * @package @shared/services
 */

import { logger } from '../utils/logger';
import { supabase } from '../lib/supabaseClient';
import { isAuthAbortError, readStoredAuthSnapshot } from '../lib/authStorage';

// ✅ Limpiar URL de espacios y saltos de línea (problema común en variables de entorno)
const CORE_API_URL = (import.meta.env.VITE_CORE_API_URL || 'http://localhost:4000').trim();

// ══════════════════════════════════════════
// SINGLETON — deduplicates concurrent calls + caches result
// Stored on window to survive HMR (module re-execution)
// ══════════════════════════════════════════
const _w = (typeof window !== 'undefined' ? window : {}) as any;
if (!_w.__AUTH_RESOLVE_INIT__) {
  _w.__AUTH_RESOLVE_INIT__ = true;
  _w.__AUTH_RESOLVE_PENDING__ = null as Promise<CoreAuthResolveResponse> | null;
  _w.__AUTH_RESOLVE_CACHE__ = null as CoreAuthResolveResponse | null;
  _w.__AUTH_RESOLVE_TS__ = 0;
}
const AUTH_CACHE_TTL = 30_000; // 30 seconds

export interface CoreAuthResolveResponse {
  authenticated: boolean;
  redirect: string | null;
  user: {
    id: string;
    email: string;
    user_metadata?: any;
  } | null;
  domains?: string[];
  error?: string;
}

/**
 * Singleton wrapper: deduplicates concurrent calls and caches results for 30s.
 * This is what consumers (EmpresaAuthContext, RequireDomainAccess) should call.
 *
 * - If a cached result exists and is <30s old → returns cache instantly
 * - If a request is already in flight → returns the same promise (no duplicate fetch)
 * - On 429/500/network error → falls back to supabase.auth.getSession()
 * - NEVER causes logout on transient errors
 */
export async function resolveAuth(): Promise<CoreAuthResolveResponse> {
  const now = Date.now();

  // Return cached result if fresh
  if (_w.__AUTH_RESOLVE_CACHE__ && (now - _w.__AUTH_RESOLVE_TS__) < AUTH_CACHE_TTL) {
    logger.debug('[resolveAuth] ✅ Returning cached result (age: ' + (now - _w.__AUTH_RESOLVE_TS__) + 'ms)');
    return _w.__AUTH_RESOLVE_CACHE__;
  }

  // If a request is already in flight, piggyback on it
  if (_w.__AUTH_RESOLVE_PENDING__) {
    logger.debug('[resolveAuth] ⏳ Waiting for in-flight request');
    return _w.__AUTH_RESOLVE_PENDING__;
  }

  // Fire new request
  _w.__AUTH_RESOLVE_PENDING__ = _resolveAuthInternal()
    .then((result) => {
      let resolved = result;
      if (!resolved.authenticated) {
        const snap = readStoredAuthSnapshot();
        if (snap.user && snap.accessToken) {
          resolved = {
            authenticated: true,
            redirect: null,
            user: {
              id: snap.user.id,
              email: snap.user.email || '',
              user_metadata: snap.user.user_metadata,
            },
          };
        }
      }
      _w.__AUTH_RESOLVE_CACHE__ = resolved;
      _w.__AUTH_RESOLVE_TS__ = Date.now();
      _w.__AUTH_RESOLVE_PENDING__ = null;
      return resolved;
    })
    .catch(async (err) => {
      _w.__AUTH_RESOLVE_PENDING__ = null;
      // On any error, fallback to Supabase session
      logger.warn('[resolveAuth] Core API failed, trying Supabase fallback:', err?.message || err);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const fallback: CoreAuthResolveResponse = {
            authenticated: true,
            redirect: null,
            user: { id: session.user.id, email: session.user.email || '', user_metadata: session.user.user_metadata },
          };
          _w.__AUTH_RESOLVE_CACHE__ = fallback;
          _w.__AUTH_RESOLVE_TS__ = Date.now();
          return fallback;
        }
      } catch { /* supabase also failed */ }
      return { authenticated: false, redirect: null, user: null, error: err?.message || 'Network error' } as CoreAuthResolveResponse;
    });

  return _w.__AUTH_RESOLVE_PENDING__;
}

/** Invalidate singleton cache — call after login/logout */
export function invalidateAuthCache() {
  _w.__AUTH_RESOLVE_CACHE__ = null;
  _w.__AUTH_RESOLVE_TS__ = 0;
  _w.__AUTH_RESOLVE_PENDING__ = null;
  logger.debug('[resolveAuth] Cache invalidated');
}

/**
 * Internal implementation — actually calls Core API.
 * NOT exported. Use resolveAuth() singleton wrapper instead.
 */
async function _resolveAuthInternal(): Promise<CoreAuthResolveResponse> {
  // ✅ Declarar accessToken fuera del try para que esté disponible en el catch
  let accessToken: string | null = null;
  
  try {
    // ✅ SIEMPRE intentar obtener access_token de Supabase (DEV y PROD)
    
    // Intentar leer directamente de Supabase
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError && !isAuthAbortError(sessionError)) {
      logger.error('[resolveAuth] Error leyendo sesión de Supabase:', sessionError);
    }
    
    accessToken = session?.access_token || readStoredAuthSnapshot().accessToken || null;
    
    if (accessToken) {
      logger.debug('[resolveAuth] ✅ Token obtenido de Supabase localStorage');
    } else {
      logger.warn('[resolveAuth] ⚠️ No hay token en Supabase localStorage');
    }

    // Llamar a Core API
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Enviar token como header si está disponible (DEV y PROD)
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${CORE_API_URL}/auth/resolve`, {
      method: 'GET',
      headers,
      credentials: 'include', // Incluir cookies en PROD
    });

    if (!response.ok) {
      logger.error(`❌ Core API /auth/resolve error: ${response.status}`);
      // 429/500+ → throw so singleton wrapper triggers Supabase fallback
      // 401 → not authenticated (real auth failure, not transient)
      if (response.status === 429 || response.status >= 500) {
        throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
      }
      return {
        authenticated: false,
        redirect: null,
        user: null,
        error: `HTTP ${response.status}`,
      };
    }

    const data = await response.json();
    const apiResult = data as CoreAuthResolveResponse;

    if (!apiResult.authenticated && accessToken) {
      const snap = readStoredAuthSnapshot();
      if (snap.user) {
        return {
          authenticated: true,
          redirect: null,
          user: {
            id: snap.user.id,
            email: snap.user.email || '',
            user_metadata: snap.user.user_metadata,
          },
        };
      }
    }

    return apiResult;
  } catch (error) {
    // ✅ Diagnóstico mejorado para errores de CORS/red
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
    const isCorsError = errorMessage.includes('Failed to fetch') || errorMessage.includes('CORS');
    const isAbortError = errorMessage.includes('abort') || errorMessage.includes('AbortError');

    logger.warn('⚠️ Core API /auth/resolve no disponible:', {
      error: errorMessage,
      isCorsError,
      isAbortError,
      coreApiUrl: CORE_API_URL,
      hasAccessToken: !!accessToken,
    });

    // ✅ THROW para que el singleton wrapper ejecute el fallback de Supabase
    // Si retornamos { authenticated: false }, se cachea como "no autenticado" y
    // el fallback de supabase.auth.getSession() NUNCA se ejecuta.
    throw error;
  }
}

/**
 * Notifica al Core API después de login exitoso
 * Establece cookie httpOnly para SSO cross-domain (solo en PROD).
 *
 * @param accessToken - Token de acceso de Supabase
 */
export async function notifyCoreAPILogin(accessToken: string): Promise<boolean> {
  // En DEV: no-op (la cookie solo se usa en PROD; evita request que retrasa el handoff)
  if (import.meta.env.DEV) {
    return true;
  }
  try {
    const response = await fetch(`${CORE_API_URL}/auth/set-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ access_token: accessToken }),
    });

    if (!response.ok) {
      logger.error(`❌ Error notificando Core API del login: ${response.status}`);
      return false;
    }

    logger.info('✅ Core API notificado del login exitoso');
    return true;
  } catch (error) {
    logger.error('❌ Error notificando Core API del login:', error);
    return false;
  }
}
